import { createAsyncThunk } from "@reduxjs/toolkit";
import { loadCountries, loadProviders } from "../common/commonActions";
import { Track } from "../../interfaces/dtos/track";
import { RootState } from "../store";
import { TrackState } from "./trackSlice";
import { apiConfigDelete, apiConfigGet, apiConfigPost, apiConfigPostFile, apiConfigPut } from "../../utils/apiConfig";
import { apiUrls } from "../../components/shared/apiUrls";
import { CommonCanUpdate } from "../../utils/commonRules";

// loadTracks
export const loadTracks = createAsyncThunk(
    'track/loadTracks',
    async (provider: string, { dispatch, getState }) => {

        // load countries
        await dispatch(loadCountries());

        // load suppliers
        await dispatch(loadProviders());

        const response = await apiConfigGet<Track[]>(
            `${apiUrls.trackCode.getAll(provider)}`
        );

        const { track: trackState } = getState() as RootState;
        const otherTracks = trackState.tracks.filter((t) => t.provider !== provider);

        return [...otherTracks, ...mapTracks(response)];
    }
);

interface LoadTrackArgs {
    provider: string;
    trackCodeKey: string;
}

// loadTrack
export const loadTrack = createAsyncThunk(
    'track/loadTrack',
    async ({ provider, trackCodeKey }: LoadTrackArgs, { dispatch, getState }) => {

        // load countries
        await dispatch(loadCountries());

        // load suppliers
        await dispatch(loadProviders());

        const response = await apiConfigGet<Track>(
            `${apiUrls.trackCode.get(provider, trackCodeKey)}`
        );

        const { track: trackState } = getState() as RootState;
        const track = mapTrack(response);
        const otherTracks = trackState.tracks.filter((t) => t.key !== track.key);

        return [...otherTracks, track];
    }
);

// saveTrack
export const saveTrack = createAsyncThunk(
    'track/saveTrack',
    async (track: Track, { getState }) => {
        const { track: trackState } = getState() as RootState;
        const forNew = track.key === undefined || track.key === "";
        track = mapTrack(track);
        if (forNew) {
            track = setDefaultTrackValuesForNew(track);
            const existingTrack = trackState.tracks.find((t) => t.key === track.key);
            if (existingTrack) {
                throw new Error('The track combination of code, country, category and provider already exists.');
            }
        }

        const apiCall = forNew ? apiConfigPost : apiConfigPut;
        await apiCall(apiUrls.trackCode.save, track);

        const otherTracks = trackState.tracks.filter((t) => t.key !== track.key);

        return [...otherTracks, mapTrack(track)];
    }
);

const setDefaultTrackValuesForNew = (track: Track): Track => {
    const canUpdateTrack = CommonCanUpdate(track.provider, track.categoryCode);
    return {
        ...track,
        key: createKey(track),
        // For tracks that you can't update - default to active (as you can't change it later)
        isTrackActive: !canUpdateTrack,
        isBPDisplay: !canUpdateTrack,
        isBPDistribute: !canUpdateTrack
    };
}

// deleteTracks
export const deleteTracks = createAsyncThunk(
    'track/deleteTracks',
    async (tracks: Track[], { getState }) => {

        await Promise.all(tracks.map(async (track) => {
            await apiConfigDelete(apiUrls.trackCode.delete(track.provider!, track.trackCodeKey!));
        }));

        const { track: trackState } = getState() as RootState;
        const newTracks = trackState.tracks.filter((t) =>
            !tracks.some((td) => t.key === td.key)
        );

        return newTracks;
    }
);

// setActive
export const setActive = createAsyncThunk(
    'track/setActive',
    async (tracksToUpdate: Track[], { getState }) => {
        const { track: trackState } = getState() as RootState;
        return setStatus(tracksToUpdate, true, trackState);
    }
);

// setInactive
export const setInactive = createAsyncThunk(
    'track/setInactive',
    async (tracksToUpdate: Track[], { getState }) => {
        const { track: trackState } = getState() as RootState;
        return setStatus(tracksToUpdate, false, trackState);
    }
);

const setStatus = async (
    tracksToUpdate: Track[], isTrackActive: boolean, trackState: TrackState
) => {
    const tracks = trackState.tracks.filter((t) =>
        tracksToUpdate.some((tu) => t.key === tu.key)
    );
    const updatedTracks = tracks.map((t) => ({ ...t, isTrackActive }));

    await Promise.all(updatedTracks.map(async (trackToUpdate) => {
        await apiConfigPut(apiUrls.trackCode.save, trackToUpdate);
    }));

    const otherTracks = trackState.tracks.filter((t) =>
        !tracksToUpdate.some((tu) => t.key === tu.key)
    );

    return [...updatedTracks, ...otherTracks];
}

interface UploadTracksFileArgs {
    provider: string;
    file?: File;
}

// uploadTracksFile
export const uploadTracksFile = createAsyncThunk(
    'track/uploadTracksFile',
    async ({ provider, file }: UploadTracksFileArgs, { dispatch }) => {
        if (file === undefined) {
            return;
        }

        const model = new FormData();
        model.append('trackCodeFile', file);

        await apiConfigPostFile<void, FormData>
            (apiUrls.trackCode.upload(provider), model, function (perc: number) {
                dispatch(updateUploadingPerc(perc));
            });
        dispatch(updateUploadingPerc(100));

        await dispatch(loadTracks(provider));
    }
);

// updateUploadingPerc
export const updateUploadingPerc = createAsyncThunk(
    'track/updateUploadingPerc',
    (perc: number | undefined) => {
        return perc;
    }
);

const mapTracks = (tracks: Track[]): Track[] => {
    return tracks.map((t) => mapTrack(t));
}

const mapTrack = (track: Track): Track => {
    return {
        ...track,
        trackCodeKey: createTrackCodeKeyFromTrack(track),
        key: createKey(track)
    }
}

export const createTrackCodeKeyFromTrack = (track: Track) => {
    return createTrackCodeKey(track.sportCode!, track.categoryCode!, track.countryCode!);
}

export const createTrackCodeKey = (sportCode: string, categoryCode: string, countryCode: string) => {
    return `${sportCode}${categoryCode}${countryCode}`;
}

export const createKey = (track: Track) => {
    return `${track.provider}_${createTrackCodeKeyFromTrack(track)}`;
}
